/**
 * Phase 36 — experiments: a time-boxed gateway test. For N days a share of a
 * container's drops goes through one gateway of its pool while the rest keep
 * the configured strategy over the other gateways; the two sides are
 * compared on one metric, and the result offers to apply the winner.
 * For Media Devoted | Created By Ciro Autiero
 */
import { DROP_GEN_HISTORY_METRICS } from "../history/history.types";

export const DROP_GEN_EXPERIMENT_KINDS = ["GATEWAY"] as const;
export type DropGenExperimentKind = (typeof DROP_GEN_EXPERIMENT_KINDS)[number];

export const DROP_GEN_EXPERIMENT_STATUSES = [
  "RUNNING",
  "DONE",
  "APPLIED",
  "STOPPED",
] as const;
export type DropGenExperimentStatus =
  (typeof DROP_GEN_EXPERIMENT_STATUSES)[number];

/** The metrics an experiment compares on — the judged rates (money waits for Phase 30). */
export const DROP_GEN_EXPERIMENT_METRICS = DROP_GEN_HISTORY_METRICS;
export type DropGenExperimentMetric =
  (typeof DROP_GEN_EXPERIMENT_METRICS)[number];

/** Under this many settled SMS on either side the result is "too early". */
export const DROP_GEN_EXPERIMENT_MIN_SMS = 300;
export const DROP_GEN_EXPERIMENT_MAX_DAYS = 30;
export const DROP_GEN_EXPERIMENT_MAX_SHARE = 50;

/** What "apply the winner" can do to the pool. */
export const DROP_GEN_EXPERIMENT_ACTIONS = ["first", "out", "share"] as const;
export type DropGenExperimentAction =
  (typeof DROP_GEN_EXPERIMENT_ACTIONS)[number];

/** Rates where a lower value is the better one. */
export const EXPERIMENT_LOWER_WINS: readonly DropGenExperimentMetric[] = [
  "botRate",
  "undeliverableRate",
];

export interface DropGenExperimentSide {
  /** "C" (the experiment gateway) or "A/B" (the others). */
  label: string;
  drops: number;
  smsSent: number;
  conversions: number;
  /** The metric over the side's bags; null when it cannot be computed. */
  value: number | null;
}

export interface DropGenExperimentResult {
  metric: DropGenExperimentMetric;
  experiment: DropGenExperimentSide;
  others: DropGenExperimentSide;
  /** Who is ahead on the metric; null when a side has no value. */
  winner: "experiment" | "others" | "tie" | null;
  /** The experiment gateway vs the others, in % of the others' value (sign = better / worse). */
  deltaPct: number | null;
  /** Under the minimum SMS on either side — the numbers are shown, no verdict. */
  tooEarly: boolean;
  /** Judged days seen (days with at least one bag on either side). */
  daysSeen: number;
  words: string;
  judgedAt: string;
}

export interface DropGenExperimentRow {
  id: string;
  containerId: string;
  containerName: string;
  kind: DropGenExperimentKind;
  routeId: number;
  routeName: string | null;
  /** Phase 40 — the LANE under test (null = started before lanes: the route). */
  gatewayRowId?: string | null;
  /** "23Telecom · AUSSIEWIN" — the lane's label; null when unknown. */
  laneLabel?: string | null;
  sharePct: number;
  /** Recipient-local days, inclusive. */
  startDate: string;
  endDate: string;
  days: number;
  /** 1-based day of the experiment today; 0 before it starts, > days after. */
  dayOf: number;
  metric: DropGenExperimentMetric;
  status: DropGenExperimentStatus;
  result: DropGenExperimentResult | null;
  appliedAction: DropGenExperimentAction | null;
  createdAt: string;
  stoppedAt: string | null;
}

export interface DropGenExperimentCreate {
  routeId: number;
  /** Phase 40 — a lane of routeId; omitted = the route's first lane. */
  gatewayRowId?: string | null;
  sharePct: number;
  days: number;
  metric: DropGenExperimentMetric;
}
